import { matchSorter } from 'match-sorter';
import * as React from 'react';
import {
  useTable,
  useFilters,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  usePagination,
} from 'react-table';

import { ReactComponent as PencilIcon } from '@src/assets/pencil.svg';

import {
  RightButton,
  CenterText,
  FormContainer,
  Container,
  FullWidthTable,
  DefaultText,
  ActiveText,
  InactiveText,
  SuspendedText
} from './styles';


const formatDate = dateString => {
  const newDate = new Date(dateString)
  const formatted = newDate.toLocaleDateString('en-US');
  
  return formatted;
};

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
  resultCount,
  setPreviousFilterValue,
}) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce(value => {
    setPreviousFilterValue(value);
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <FormContainer>
      Search:{' '}
      <input
        className='full-width'
        value={value || ''}
        key='search-sanctions'
        onChange={e => {
          setValue(e.target.value);
          onChange(e.target.value);
        }}
        placeholder={`${count.toLocaleString()} records...`}
        style={{
          fontSize: '1.1rem',
          border: '0',
        }}
      />
      {globalFilter && (
        <span>
          {resultCount} {resultCount == 1 ? 'result' : 'results'}
        </span>
      )}
    </FormContainer>
  );
}

const renderStatusState = status => {
  let statusIcon = <DefaultText>N/A</DefaultText>;
  let renderPencil = false;

  if (status === 'Confirmed') {
    statusIcon = <ActiveText>Active</ActiveText>;
    renderPencil = true;
  } else if (status === 'Rejected') {
    statusIcon = <InactiveText>Inactive</InactiveText>;
    renderPencil = true;
  } else if (status === 'Suspended') {
    statusIcon = <SuspendedText>Suspended</SuspendedText>;
    renderPencil = false;
  } else if (status === 'Current') {
    statusIcon = <DefaultText>Current</DefaultText>;
    renderPencil = true;
  }

  return (
    <span>
      {statusIcon}{' '}
      <span className='hover-icon'>{renderPencil && <PencilIcon />}</span>
    </span>
  );
};

function fuzzyTextFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [row => row.values[id]] });
}

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = val => !val;

export default function GenericTable({
  lists,
  setSelectedListId,
  filterValue,
}) {
  const [selectedUser, setSelectedUser] = React.useState('');
  const [previousFilterValue, setPreviousFilterValue] = React.useState('');
  const closeModal = () => setSelectedUser('');
  const columns = React.useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'list_name',
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ cell: { value } }) => renderStatusState(value),
      },
      {
        Header: 'ID',
        accessor: 'list_id',
        filter: 'fuzzyText',
      },
      {
        Header: 'Introduced',
        accessor: 'created_on',
        Cell: ({ cell: { value } }) => formatDate(value),
        sortType: (rowA, rowB, id, desc) => {
          if (
            new Date(rowA.values[id]).getTime() >
            new Date(rowB.values[id]).getTime()
          )
            return 1;
          if (
            new Date(rowB.values[id]).getTime() >
            new Date(rowA.values[id]).getTime()
          )
            return -1;
          return 0;
        },
      },
      {
        Header: 'Last Status Change',
        accessor: 'updated_on',
        Cell: ({ cell: { value } }) => formatDate(value),
        sortType: (rowA, rowB, id, desc) => {
          if (
            new Date(rowA.values[id]).getTime() >
            new Date(rowB.values[id]).getTime()
          )
            return 1;
          if (
            new Date(rowB.values[id]).getTime() >
            new Date(rowA.values[id]).getTime()
          )
            return -1;
          return 0;
        },
      },
    ],
    []
  );
  const data = React.useMemo(() => lists, [lists, selectedUser]);
  const filterTypes = React.useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      fuzzyText: fuzzyTextFilterFn,
      // Or, override the default text filter to use
      // "startWith"
      text: (rows, id, filterValue) => {
        return rows.filter(row => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      },
    }),
    []
  );
  const defaultColumn = React.useMemo(
    () => ({
      Filter: undefined,
    }),
    []
  );

  const initialState = {
    globalFilter: previousFilterValue,
  };
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    state,
    rows,
    prepareRow,
    page,
    pageCount,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    ...other
  } = useTable(
    { columns, data, initialState },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  // TODO fix typedefs and avoid this hack
  const {
    setGlobalFilter,
    globalFilteredRows,
    preGlobalFilteredRows,
    sortedRows,
  } = other;
  const { globalFilter, pageIndex } = state;

  return (
    <div>
      <div>
        <GlobalFilter
          setPreviousFilterValue={setPreviousFilterValue}
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          preGlobalFilteredRows={preGlobalFilteredRows}
          resultCount={globalFilteredRows.length}
        />
      </div>
      <FullWidthTable>
        <thead>
          {headerGroups.map((headerGroup, trIndex) => (
            <tr key={`tr-${trIndex}`}>
              {headerGroup.headers.map((column, thIndex) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  key={`th-${thIndex}`}
                >
                  {column.render('Header')}
                  <span>
                    {column.isSorted
                      ? column.isSortedDesc
                        ? ' 🔽'
                        : ' 🔼'
                      : ''}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {page.map(row => {
            prepareRow(row);
            const onClick = () => {
              setSelectedListId(row.values.list_id);
            };
            return (
              <tr key={row.values.list_id} onClick={onClick}>
                {row.cells.map((cell, tdIndex) => {
                  return <td key={`td-${tdIndex}`}>{cell.render('Cell')}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </FullWidthTable>

      <div>
        <CenterText>
          Page {pageIndex + 1} of {pageCount}
        </CenterText>
        <button disabled={!canPreviousPage} onClick={previousPage}>
          prev
        </button>
        <RightButton disabled={!canNextPage} onClick={nextPage}>
          next
        </RightButton>
      </div>
    </div>
  );
}
